/**
 * Vampires of Tokyo
 *
 * An experimental, mood-driven interactive experience.
 */

import type { Scene } from '@/types';
import { initAudio, setAmbient, toggleMute, duckAmbient, restoreAmbient } from '@/lib/audio';
// Custom player shelved — see scenes/music.shelved.ts
import { navigateTo, loadProgress, getCurrentScene, discoverSecret } from '@/lib/scene';
import { saveSubscriber } from '@/lib/supabase';
import { initializeScenes } from '@/scenes';
import { initDrip, destroyDrip, loadDripItems } from '@/scenes/drip';
import { loadBunkerItems } from '@/scenes/relic-bunker';
import { initMore, destroyMore } from '@/scenes/more';
import { playGoDeeperIntro, cancelGoDeeperIntro, initGoDeeper, destroyGoDeeper } from '@/scenes/go-deeper';

async function init(): Promise<void> {
  // eslint-disable-next-line no-console
  console.log('🦇 Vampires of Tokyo initializing...');

  // Initialize systems
  initAudio();
  initializeScenes();

  // Check if user has already entered (has email stored)
  const hasEntered = localStorage.getItem('vampires_email');

  if (hasEntered) {
    // Skip gate, load progress
    document.getElementById('landing-gate')?.remove();
    loadProgress();

    // Browsers require a user gesture for audio — start ambient on first interaction
    const startAmbientOnGesture = (): void => {
      setAmbient('/audio/ambient/neon-dreams.mp3');
      document.removeEventListener('click', startAmbientOnGesture);
      document.removeEventListener('keydown', startAmbientOnGesture);
    };
    document.addEventListener('click', startAmbientOnGesture);
    document.addEventListener('keydown', startAmbientOnGesture);

    if (!getCurrentScene()) {
      await navigateTo('home');
    }
  } else {
    // Show landing gate
    setupLandingGate();
  }

  // Set up global event listeners
  setupInteractions();

  // eslint-disable-next-line no-console
  console.log('🌙 Ready.');
}

function setupInteractions(): void {
  let previousSceneId: string | null = null;

  // Listen for scene loads to set up audio
  globalThis.addEventListener('scene:loaded', (e) => {
    const scene = (e as CustomEvent<Scene>).detail;
    if (scene.atmosphere.ambientAudio) {
      setAmbient(scene.atmosphere.ambientAudio);
    }

    // Duck ambient on music scene (Spotify iframe); restore when leaving
    if (scene.id === 'music') {
      duckAmbient();
    } else if (previousSceneId === 'music') {
      restoreAmbient();
    }
    previousSceneId = scene.id;

    // Music player lifecycle — shelved, see scenes/music.shelved.ts

    // Drip carousel lifecycle
    destroyDrip();
    if (scene.id === 'drip') {
      void loadDripItems().then((productItems) => {
        initDrip(productItems);
      });
    } else if (scene.id === 'relic-bunker') {
      void loadBunkerItems().then((productItems) => {
        initDrip(productItems);
      });
    }

    // More page lifecycle
    destroyMore();
    if (scene.id === 'more') {
      initMore();
    }

    // Go Deeper lifecycle
    cancelGoDeeperIntro();
    destroyGoDeeper();
    if (scene.id === 'go-deeper') {
      initGoDeeper();
    }
  });

  // Audio toggle button
  const audioToggle = document.getElementById('audio-toggle');
  if (audioToggle) {
    audioToggle.addEventListener('click', () => {
      const isMuted = toggleMute();
      audioToggle.classList.toggle('muted', isMuted);
    });
  }

  // Interactive elements
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.dataset['interaction']) {
      handleInteraction(target.dataset['interaction']);
    }
  });

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      toggleMenu();
    }
  });
}

function handleInteraction(interactionId: string): void {
  const scene = getCurrentScene();
  if (!scene) return;

  const interaction = scene.interactions.find((i) => i.id === interactionId);
  if (!interaction) return;

  // Handle different action types
  switch (interaction.action.type) {
    case 'navigate':
      if (interaction.action.sceneId === 'go-deeper') {
        playGoDeeperIntro();
      } else {
        void navigateTo(interaction.action.sceneId);
      }
      break;
    case 'reveal':
      revealContent(interaction.action.content);
      break;
    case 'secret':
      discoverSecret(interaction.action.secretId);
      break;
    // Add more handlers as needed
  }
}

/**
 * Display revealed content with atmospheric fade-in/auto-dismiss.
 * Content appears for ~4 seconds then fades out to maintain mood.
 */
function revealContent(content: string): void {
  // Find or create reveal container
  let container = document.getElementById('reveal-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'reveal-container';
    container.className = 'reveal';
    document.getElementById('scene-container')?.appendChild(container);
  }

  // Set content and trigger fade-in
  container.textContent = content;
  // Force reflow to restart animation
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  container.offsetWidth;
  container.classList.add('visible');

  // Auto-dismiss after ~4 seconds
  setTimeout(() => {
    container.classList.remove('visible');
  }, 4000);
}

function toggleMenu(): void {
  const menu = document.getElementById('menu');
  menu?.classList.toggle('hidden');
}

/**
 * Set up the landing gate email form.
 * On submit, stores email locally and transitions to main experience.
 */
function setupLandingGate(): void {
  const form = document.getElementById('email-gate-form') as HTMLFormElement | null;
  const gate = document.getElementById('landing-gate');

  if (!form || !gate) return;

  // Start ambient audio on first interaction (browsers require user gesture)
  const startAudio = (): void => {
    setAmbient('/audio/ambient/neon-dreams.mp3');
    gate.removeEventListener('click', startAudio);
  };
  gate.addEventListener('click', startAudio);

  form.addEventListener('submit', (e): void => {
    e.preventDefault();

    const emailInput = document.getElementById('gate-email') as HTMLInputElement | null;
    const email = emailInput?.value.trim();

    if (!email) return;

    // Store email locally and to Supabase
    localStorage.setItem('vampires_email', email);
    void saveSubscriber(email, 'landing_gate');

    // Replace form with success message
    const message = document.createElement('span');
    message.className = 'gate-message';
    message.textContent = 'We will find you...';
    form.replaceWith(message);

    // Fade out after a moment
    setTimeout(() => {
      gate.style.transition = 'opacity 1200ms ease-out';
      gate.style.opacity = '0';
    }, 1500);

    setTimeout(() => {
      gate.remove();
      void navigateTo('home');
    }, 2700);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  await new Promise<void>((resolve) => {
    document.addEventListener('DOMContentLoaded', () => {
      resolve();
    });
  });
}

await init();
